/**
 * Centralized analytics module — the ONLY place components/pages should
 * import analytics from. Add new providers (Google Ads, Meta Pixel,
 * LinkedIn Insight Tag, Microsoft Advertising UET) by:
 *   1. Creating an adapter file here (e.g. `metaPixel.js`) mirroring the
 *      shape of `ga4.js` (an `initX(id)` + `sendXEvent(...)`).
 *   2. Calling its `initX(...)` from `initAnalytics()` below.
 *   3. Fanning out to it from `trackEvent()` / `trackPageView()` below.
 * Components/pages never talk to gtag/Clarity/etc. directly — only to
 * `trackPageView` / `trackEvent` from this file — so adding a provider
 * never requires touching application code.
 */
import { analyticsConfig } from "./config";
import { initGa4, sendGa4Event, sendGa4PageView } from "./ga4";
import { initClarity, trackClarityEvent } from "./clarity";

let initialized = false;
let lastPageKey = null;

export function initAnalytics() {
  if (initialized || !analyticsConfig.isProduction) return;
  initialized = true;

  initGa4(analyticsConfig.ga4MeasurementId);
  initClarity(analyticsConfig.clarityProjectId);

  // --- Future providers hook in here, e.g.: ---
  // initGoogleAds(analyticsConfig.googleAdsId);
  // initMetaPixel(analyticsConfig.metaPixelId);
  // initLinkedInInsightTag(analyticsConfig.linkedInPartnerId);
  // initMicrosoftUet(analyticsConfig.uetTagId);
}

/**
 * Fires exactly one pageview per unique path, even if called from
 * multiple effects/StrictMode double-invokes/back-to-back renders —
 * this `lastPageKey` guard is what prevents duplicate pageview tracking.
 */
export function trackPageView(pathname) {
  if (!analyticsConfig.isProduction) return;
  initAnalytics();

  if (pathname === lastPageKey) return;
  lastPageKey = pathname;

  sendGa4PageView(pathname);
  // Clarity tracks sessions/heatmaps automatically; no manual pageview call needed.
}

/**
 * Generic custom-event hook for future use (button clicks, conversions,
 * etc). Only pass non-sensitive, bounded values — never tokens, emails,
 * names, or raw API responses.
 */
export function trackEvent(name, params = {}) {
  if (!analyticsConfig.isProduction) return;
  initAnalytics();

  sendGa4Event(name, params);
  trackClarityEvent(name);
}
